import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, CircleAlert, Info, X } from "./icons.js";
import { PixelDrop } from "./pixel-art/pixel-drop.js";
import type { Toast } from "./toast.js";

/**
 * Where toasts are drawn: bottom centre, newest in front, the rest peeking out
 * behind it, opening into a column while the pointer or focus is on it.
 *
 * Mounted on `document.body`, and only while there is a toast. That is what
 * lets a toast raised inside a dialog be heard and clicked: a Radix dialog
 * marks everything already on the page `aria-hidden` and turns its pointer
 * events off when it opens. A dock that did not exist then escapes both.
 */

/** A confirmation leaves on its own; an error waits to be read and closed. */
const LIFETIME_MS = 5000;

const MARKS = { done: Check, error: CircleAlert, info: Info } as const;

export function ToastDock({
  toasts,
  onClose,
}: {
  readonly toasts: readonly Toast[];
  readonly onClose: (id: number) => void;
}) {
  // Here, not in the provider, so it resets when the last toast goes and the
  // dock unmounts under a pointer that never got to leave it.
  const [held, setHeld] = useState(false);

  return createPortal(
    <section
      className={held ? "toast-dock toast-dock-open" : "toast-dock"}
      aria-label="Notifications"
      // Inline, like the `pointer-events: none` a Radix dialog puts on body:
      // a toast raised from inside a drawer or modal is outside it, and
      // without this its Undo could be seen and never pressed.
      style={{ pointerEvents: "auto" }}
      onMouseEnter={() => {
        setHeld(true);
      }}
      onMouseLeave={() => {
        setHeld(false);
      }}
      onFocus={() => {
        setHeld(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setHeld(false);
      }}
    >
      {toasts.map((toast, index) => (
        <ToastCard
          key={toast.id}
          toast={toast}
          depth={toasts.length - 1 - index}
          held={held}
          onClose={onClose}
        />
      ))}
    </section>,
    document.body,
  );
}

function ToastCard({
  toast,
  depth,
  held,
  onClose,
}: {
  readonly toast: Toast;
  /** 0 in front; 1 and 2 peek out behind it until the dock is opened. */
  readonly depth: number;
  readonly held: boolean;
  readonly onClose: (id: number) => void;
}) {
  const left = useRef(LIFETIME_MS);

  // A timer, not data: the one kind of effect this codebase keeps. Holding the
  // dock stops the clock and keeps what was left of it.
  useEffect(() => {
    if (toast.tone === "error" || held) return;
    const started = Date.now();
    const timer = window.setTimeout(() => {
      onClose(toast.id);
    }, left.current);
    return () => {
      window.clearTimeout(timer);
      left.current -= Date.now() - started;
    };
  }, [toast, held, onClose]);

  const Mark = MARKS[toast.tone];
  const behind =
    depth === 0 ? "" : ` toast-behind toast-behind-${String(Math.min(depth, 2))}`;

  return (
    <div
      className={`toast toast-${toast.tone}${behind}`}
      role={toast.tone === "error" ? "alert" : "status"}
    >
      {/* Decoration only, behind the X; never on an error (DESIGN.md §9, "Pixel art"). */}
      {toast.tone === "error" ? null : (
        <PixelDrop
          shape="corner"
          clear=".toast-title, .toast-detail, .toast-action"
          cell={5}
        />
      )}
      <span className="toast-mark" aria-hidden="true">
        <Mark />
      </span>
      <span className="toast-words">
        <span className="toast-title">{toast.title}</span>
        {toast.detail === undefined ? null : (
          <span className="toast-detail">{toast.detail}</span>
        )}
      </span>
      {toast.action === undefined ? null : (
        <button type="button" className="toast-action" onClick={toast.action.run}>
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        className="toast-close"
        aria-label="Dismiss"
        onClick={() => {
          onClose(toast.id);
        }}
      >
        <X aria-hidden="true" />
      </button>
    </div>
  );
}
